import React, { useCallback, useEffect, useMemo, useState } from "react";
import Api from "../utils/Api";
import SwalHelper from "../utils/Swal";
import LoadingOverlay from "../components/LoadingOverlay";
import {
  MdAdd,
  MdDelete,
  MdEdit,
  MdRefresh,
  MdSearch,
  MdVisibility,
} from "react-icons/md";
import ModalCreateCompletion from "../components/modals/completion/ModalCreateCompletion";
import ModalDetailCompletion from "../components/modals/completion/ModalDetailCompletion";
import ModalEditCompletion from "../components/modals/completion/ModalEditCompletion";
import DocumentPreviewModal from "../components/modals/DocumentPreviewModal";
import { STAGE_BADGES, STAGE_LABELS } from "../utils/WorkBadge";
import { formatFullDate, formatTanggalIndoLengkap } from "../utils/Helpers";

const STAGE_FILTER_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "CLOSED", label: "Closed" },
];

const PER_PAGE_OPTIONS = [25, 50, 100];

const Completion = () => {
  const [dataCompletion, setDataCompletion] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedCompletionId, setSelectedCompletionId] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  // Document Preview
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState("");
  const [selectedDocumentTitle, setSelectedDocumentTitle] =
    useState("Preview Dokumen BA");

  const [filter, setFilter] = useState({
    stage: "ACTIVE",
    search: "",
    id_client: "",
    id_work_item: "",
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total: 0,
    total_pages: 1,
  });

  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });

  // ============================================================
  // FETCH CLIENT OPTIONS
  // ============================================================

  const fetchClients = useCallback(async () => {
    try {
      const response = await Api.get("/work-item/master/clients/options");

      if (response.data?.success) {
        setClients(response.data?.data || []);
      } else {
        SwalHelper.error(
          response.data?.message || "Gagal mengambil data client.",
        );
      }
    } catch (error) {
      console.error("Gagal mengambil data client:", error);

      SwalHelper.error(
        error.response?.data?.message || "Gagal mengambil data client.",
      );
    }
  }, []);

  // ============================================================
  // FETCH BA
  // ============================================================

  const fetchCompletions = useCallback(async () => {
    setLoading(true);

    try {
      const params = {
        stage: filter.stage,
      };

      if (filter.search.trim()) {
        params.search = filter.search.trim();
      }

      if (filter.id_work_item) {
        params.id_work_item = Number(filter.id_work_item);
      }

      if (filter.id_client) {
        params.id_client = Number(filter.id_client);
      }

      if (filter.stage === "CLOSED") {
        params.page = page;
        params.per_page = perPage;
      }

      const response = await Api.get("/work-item/completion", {
        params,
      });

      if (response.data?.success) {
        const responseData = response.data?.data || {};
        const items = responseData.items || [];

        setDataCompletion(items);

        if (filter.stage === "CLOSED") {
          const pageInfo = responseData.pagination || {};

          setPagination({
            page: pageInfo.page || page,
            per_page: pageInfo.per_page || perPage,
            total: pageInfo.total || 0,
            total_pages: pageInfo.total_pages || 1,
          });
        } else {
          setPagination({
            page: 1,
            per_page: items.length || perPage,
            total: items.length,
            total_pages: 1,
          });
        }
      } else {
        setDataCompletion([]);

        SwalHelper.error(response.data?.message || "Gagal mengambil data BA.");
      }
    } catch (error) {
      console.error("Gagal mengambil data BA:", error);

      setDataCompletion([]);

      SwalHelper.error(
        error.response?.data?.message || "Gagal mengambil data BA.",
      );
    } finally {
      setLoading(false);
    }
  }, [filter, page, perPage]);

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchCompletions();
  }, [fetchCompletions]);

  // ============================================================
  // FILTER
  // ============================================================

  const handleFilterChange = (key, value) => {
    setPage(1);

    setFilter((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleResetFilter = () => {
    setPage(1);

    setFilter((current) => ({
      stage: current.stage,
      search: "",
      id_client: "",
      id_work_item: "",
    }));
  };

  // ============================================================
  // REFRESH
  // ============================================================

  const handleRefresh = () => {
    fetchCompletions();
  };

  // ============================================================
  // PAGINATION
  // ============================================================

  const handlePerPageChange = (value) => {
    setPerPage(Number(value));
    setPage(1);
  };

  // ============================================================
  // SORTING
  // ============================================================

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  const displayedData = useMemo(() => {
    const data = [...dataCompletion];

    data.sort((a, b) => {
      const { key, direction } = sortConfig;

      let valueA = a[key];
      let valueB = b[key];

      if (key === "ba_date" || key === "created_at") {
        valueA = new Date(valueA || 0).getTime();
        valueB = new Date(valueB || 0).getTime();
      } else {
        valueA = String(valueA || "").toLowerCase();
        valueB = String(valueB || "").toLowerCase();
      }

      if (valueA < valueB) {
        return direction === "asc" ? -1 : 1;
      }

      if (valueA > valueB) {
        return direction === "asc" ? 1 : -1;
      }

      return 0;
    });

    return data;
  }, [dataCompletion, sortConfig]);

  // ============================================================
  // CREATE
  // ============================================================

  const handleCreate = () => {
    setShowCreate(true);
  };

  // ============================================================
  // DETAIL
  // ============================================================

  const handleDetail = (item) => {
    setSelectedCompletionId(item.id_completion);
    setShowDetail(true);
  };

  // ============================================================
  // EDIT
  // ============================================================

  const handleEdit = (item) => {
    setSelectedCompletionId(item.id_completion);
    setShowEdit(true);
  };

  // ============================================================
  // DELETE
  // ============================================================

  const handleDelete = async (item) => {
    const confirmed = await SwalHelper.confirm({
      title: "Nonaktifkan BA?",
      message: `BA "${item.ba_number}" akan dinonaktifkan.`,
      confirmText: "Ya, Nonaktifkan",
      cancelText: "Batal",
    });

    if (!confirmed) return;

    try {
      setLoading(true);

      const response = await Api.delete(
        `/work-item/completion/${item.id_completion}`,
      );

      if (response.data?.success) {
        await SwalHelper.success(
          response.data?.message || "BA berhasil dinonaktifkan.",
        );

        await fetchCompletions();
      } else {
        SwalHelper.error(response.data?.message || "Gagal menonaktifkan BA.");
      }
    } catch (error) {
      console.error("Gagal menonaktifkan BA:", error);

      SwalHelper.error(
        error.response?.data?.message || "Gagal menonaktifkan BA.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // DOCUMENT PREVIEW
  // ============================================================

  const handleDocumentPreview = (item) => {
    if (!item.document_url) {
      SwalHelper.info("Dokumen BA tidak tersedia.");
      return;
    }

    setSelectedDocumentUrl(item.document_url);
    setSelectedDocumentTitle(
      item.ba_number ? `Dokumen BA - ${item.ba_number}` : "Preview Dokumen BA",
    );
    setShowDocumentPreview(true);
  };

  const handleCloseDocumentPreview = () => {
    setShowDocumentPreview(false);
    setSelectedDocumentUrl("");
    setSelectedDocumentTitle("Preview Dokumen BA");
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      {loading && <LoadingOverlay message="Memuat Berita Acara..." />}

      <div className="space-y-3 pb-3 font-poppins">
        {/* ======================================================
            HEADER
        ====================================================== */}

        <div className="flex flex-col items-start justify-between gap-4 px-1 md:flex-row md:items-center">
          <div>
            <h1 className="text-xl font-black uppercase tracking-tighter text-custom-gelap dark:text-white">
              Berita Acara
            </h1>

            <p className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400">
              Completion / BA Management
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 rounded-2xl border border-gray-100 bg-white px-4 py-2.5 text-[9px] font-black uppercase tracking-widest text-custom-gelap shadow-sm transition-all hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/5 dark:bg-custom-gelap dark:text-white"
            >
              <MdRefresh size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              type="button"
              onClick={handleCreate}
              className="flex items-center gap-2 rounded-2xl bg-custom-merah-terang px-5 py-2.5 text-[9px] font-black uppercase tracking-widest text-white shadow-lg transition-all hover:scale-105 active:scale-95"
            >
              <MdAdd size={16} />
              BA Baru
            </button>
          </div>
        </div>

        {/* ======================================================
            FILTER
        ====================================================== */}

        <div className="rounded-2xl border border-gray-100 bg-white p-4 dark:border-white/5 dark:bg-custom-gelap">
          <div className="flex items-center gap-3 overflow-x-auto">
            {/* Stage */}

            <div className="flex shrink-0 items-center rounded-xl bg-gray-100 p-1 dark:bg-white/5">
              {STAGE_FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleFilterChange("stage", option.value)}
                  className={`rounded-lg px-4 py-2 text-[9px] font-black uppercase tracking-widest transition-all ${
                    filter.stage === option.value
                      ? "bg-custom-merah-terang text-white shadow-sm"
                      : "text-gray-400 hover:text-custom-gelap dark:hover:text-white"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Search */}

            <div className="relative min-w-[280px] flex-1">
              <MdSearch
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                value={filter.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Cari nomor BA / pekerjaan / client..."
                className="h-10 w-full rounded-xl border border-gray-100 bg-gray-50 pl-9 pr-3 text-[10px] font-bold text-custom-gelap outline-none placeholder:text-gray-400 focus:border-custom-merah-terang dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </div>

            {/* Client */}

            <select
              value={filter.id_client}
              onChange={(e) => handleFilterChange("id_client", e.target.value)}
              className="h-10 w-44 shrink-0 cursor-pointer rounded-xl border border-gray-100 bg-gray-50 px-3 text-[10px] font-black text-custom-gelap outline-none focus:border-custom-merah-terang dark:border-white/10 dark:bg-white/5 dark:text-white"
            >
              <option value="">Semua Client</option>

              {clients.map((client) => (
                <option key={client.id_client} value={client.id_client}>
                  {client.name}
                </option>
              ))}
            </select>

            {/* Work Item */}

            <input
              type="number"
              value={filter.id_work_item}
              onChange={(e) =>
                handleFilterChange("id_work_item", e.target.value)
              }
              placeholder="ID Work Item"
              className="h-10 w-36 shrink-0 rounded-xl border border-gray-100 bg-gray-50 px-3 text-[10px] font-black text-custom-gelap outline-none placeholder:text-gray-400 focus:border-custom-merah-terang dark:border-white/10 dark:bg-white/5 dark:text-white"
            />

            {/* Reset */}

            <button
              type="button"
              onClick={handleResetFilter}
              className="flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl bg-gray-100 px-4 text-[9px] font-black uppercase tracking-widest text-custom-gelap transition-all hover:bg-gray-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
            >
              <MdRefresh size={15} />
              Reset
            </button>
          </div>
        </div>

        {/* ======================================================
            TABLE
        ====================================================== */}

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white dark:border-white/5 dark:bg-custom-gelap">
          <div className="overflow-auto max-h-[calc(100vh-260px)]">
            <table className="w-full min-w-[1050px]">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-custom-gelap">
                <tr className="border-b border-gray-100 bg-gray-50 dark:border-white/5 dark:bg-white/5">
                  <th className="w-12 px-4 py-3 text-center text-[10px] font-black uppercase tracking-wider text-gray-400">
                    #
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-gray-400"
                    onClick={() => handleSort("ba_number")}
                  >
                    No. BA
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-gray-400"
                    onClick={() => handleSort("work_name")}
                  >
                    Nama Pekerjaan
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-gray-400"
                    onClick={() => handleSort("client_name")}
                  >
                    PIC - Client
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-gray-400"
                    onClick={() => handleSort("ba_date")}
                  >
                    Tanggal BA
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center text-[10px] font-black uppercase tracking-wider text-gray-400">
                    Lampiran
                  </th>
                  <th className="px-4 py-3 text-center text-[10px] font-black uppercase tracking-wider text-gray-400">
                    Aksi
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                {displayedData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center">
                      <p className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                        Tidak ada data BA
                      </p>

                      <p className="mt-1 text-[9px] font-bold text-gray-400">
                        Data berita acara belum tersedia.
                      </p>
                    </td>
                  </tr>
                ) : (
                  displayedData.map((item, index) => (
                    <tr
                      key={item.id_completion}
                      className="transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                    >
                      {/* # */}

                      <td className="px-4 py-3 text-center align-center text-[11px] font-bold text-gray-400">
                        {filter.stage === "CLOSED"
                          ? (pagination.page - 1) * pagination.per_page +
                            index +
                            1
                          : index + 1}
                      </td>

                      {/* Nomor BA */}

                      <td className="px-4 py-3 align-top">
                        <button
                          type="button"
                          onClick={() => handleDetail(item)}
                          className="whitespace-nowrap text-[11px] font-black text-custom-merah-terang hover:underline"
                        >
                          {item.ba_number || "-"}
                        </button>

                        <p className="mt-0.5 text-[9px] font-bold text-gray-400">
                          {formatTanggalIndoLengkap(item.created_at)}
                        </p>
                      </td>

                      {/* Pekerjaan */}

                      <td className="max-w-[350px] px-4 py-3 align-top">
                        {/* Work Number + Work Stage */}
                        <div className="flex items-center gap-2">
                          <span className="whitespace-nowrap text-[9px] font-bold text-gray-400">
                            {item.work_number || "-"}
                          </span>

                          {item.stage && (
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[8px] font-black uppercase tracking-wide ${
                                STAGE_BADGES[item.stage] ||
                                "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300"
                              }`}
                            >
                              {STAGE_LABELS[item.stage] || item.stage}
                            </span>
                          )}
                        </div>

                        {/* Work Name */}
                        <p
                          className="mt-1 line-clamp-2 text-[10px] font-black leading-4 text-custom-gelap dark:text-white"
                          title={item.work_name}
                        >
                          {item.work_name || "-"}
                        </p>
                      </td>

                      {/* Client */}

                      <td className="px-4 py-3 align-top">
                        <p className="text-[11px] font-black text-custom-gelap dark:text-white whitespace-nowrap">
                          Pak {item.client_pic_name || "-"}
                        </p>
                        <p className="text-[9px] text-gray-400 font-bold mt-0.5 whitespace-nowrap">
                          {item.client_name || "-"}
                        </p>
                      </td>

                      {/* Tanggal BA */}

                      <td className="whitespace-nowrap px-4 py-3 align-top">
                        {(() => {
                          const date = formatFullDate(item.ba_date);
                          if (!date) {
                            return (
                              <span className="text-[10px] font-bold text-gray-400">
                                -
                              </span>
                            );
                          }
                          return (
                            <div className="flex flex-col">
                              <span className="text-[9px] font-black uppercase tracking-[1.5px] dark:text-white">
                                {date.dayName}
                              </span>
                              <span className="mt-0.5 text-[12px] font-black leading-4 whitespace-nowrap text-custom-gelap dark:text-white">
                                {date.date}
                              </span>
                            </div>
                          );
                        })()}
                      </td>

                      {/* ==================================================
                          DOCUMENT
                      ================================================== */}

                      <td className="px-4 py-3 align-top text-center">
                        {item.document_url ? (
                          <button
                            type="button"
                            onClick={() => handleDocumentPreview(item)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-custom-merah-terang/20 bg-custom-merah-terang/10 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-custom-merah-terang transition-all hover:-translate-y-0.5 hover:bg-custom-merah-terang/15 dark:border-custom-merah-terang/30 dark:bg-custom-merah-terang/10 dark:text-custom-cerah dark:hover:bg-custom-merah-terang/20"
                            title="Preview Dokumen"
                          >
                            <MdVisibility size={14} />
                            Preview
                          </button>
                        ) : (
                          <span className="inline-flex items-center whitespace-nowrap rounded-xl bg-gray-100 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-gray-400 dark:bg-white/5 dark:text-gray-500">
                            No File
                          </span>
                        )}
                      </td>

                      {/* Action */}

                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleDetail(item)}
                            title="Detail"
                            className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-100 text-custom-gelap transition-all hover:bg-gray-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                          >
                            <MdVisibility size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                            className="flex h-8 w-8 items-center justify-center rounded-xl bg-custom-merah-terang/10 text-custom-merah-terang transition-all hover:bg-custom-merah-terang hover:text-white"
                          >
                            <MdEdit size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            title="Delete"
                            className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-50 text-red-500 transition-all hover:bg-red-500 hover:text-white dark:bg-red-500/10"
                          >
                            <MdDelete size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ======================================================
            PAGINATION
        ====================================================== */}

        {filter.stage === "CLOSED" && pagination.total > 0 && (
          <div className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 dark:border-white/5 dark:bg-custom-gelap sm:flex-row">
            <div className="flex w-full items-center gap-3 sm:w-auto">
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-[9px] font-bold uppercase text-gray-400">
                  Tampilkan
                </span>

                <select
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                  className="h-8 cursor-pointer rounded-xl border border-gray-100 bg-gray-50 px-2 text-[10px] font-black text-custom-gelap outline-none focus:border-custom-merah-terang dark:border-white/10 dark:bg-white/5 dark:text-white"
                >
                  {PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} / halaman
                    </option>
                  ))}
                </select>
              </div>

              <div className="h-5 w-px bg-gray-100 dark:bg-white/10" />

              <p className="whitespace-nowrap text-[9px] font-bold uppercase text-gray-400">
                Menampilkan {(pagination.page - 1) * pagination.per_page + 1}–
                {Math.min(
                  pagination.page * pagination.per_page,
                  pagination.total,
                )}{" "}
                dari {pagination.total} item
              </p>
            </div>

            {pagination.total_pages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => current - 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-100 text-custom-gelap transition-all hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-30 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                  aria-label="Halaman sebelumnya"
                >
                  <span className="text-xs">‹</span>
                </button>

                {Array.from(
                  {
                    length: pagination.total_pages,
                  },
                  (_, index) => index + 1,
                )
                  .filter(
                    (pageNumber) =>
                      pageNumber === 1 ||
                      pageNumber === pagination.total_pages ||
                      Math.abs(pageNumber - page) <= 1,
                  )
                  .map((pageNumber, index, pages) => {
                    const previousPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {previousPage && pageNumber - previousPage > 1 && (
                          <span className="flex h-8 w-5 items-center justify-center text-[10px] font-black text-gray-400">
                            ...
                          </span>
                        )}

                        <button
                          type="button"
                          disabled={loading}
                          onClick={() => setPage(pageNumber)}
                          className={`flex h-8 min-w-8 items-center justify-center rounded-xl px-2 text-[10px] font-black transition-all ${
                            page === pageNumber
                              ? "bg-custom-merah-terang text-white shadow-md shadow-custom-merah-terang/20"
                              : "bg-gray-100 text-custom-gelap hover:bg-gray-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                          } disabled:cursor-not-allowed`}
                          aria-label={`Halaman ${pageNumber}`}
                          aria-current={
                            page === pageNumber ? "page" : undefined
                          }
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button
                  type="button"
                  disabled={page >= pagination.total_pages || loading}
                  onClick={() => setPage((current) => current + 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-xl bg-custom-gelap text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30 dark:bg-custom-cerah"
                  aria-label="Halaman berikutnya"
                >
                  <span className="text-xs">›</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ========================================================
          MODAL CREATE
      ======================================================== */}

      {showCreate && (
        <ModalCreateCompletion
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSuccess={fetchCompletions}
        />
      )}

      {/* ========================================================
          MODAL DETAIL
      ======================================================== */}

      {showDetail && selectedCompletionId && (
        <ModalDetailCompletion
          show={showDetail}
          completionId={selectedCompletionId}
          onClose={() => {
            setShowDetail(false);
            setSelectedCompletionId(null);
          }}
        />
      )}

      {/* ========================================================
          MODAL EDIT
      ======================================================== */}

      {showEdit && selectedCompletionId && (
        <ModalEditCompletion
          show={showEdit}
          completionId={selectedCompletionId}
          onClose={() => {
            setShowEdit(false);
            setSelectedCompletionId(null);
          }}
          onSuccess={fetchCompletions}
        />
      )}

      {/* ========================================================
          DOCUMENT PREVIEW
      ======================================================== */}

      <DocumentPreviewModal
        show={showDocumentPreview}
        url={selectedDocumentUrl}
        title={selectedDocumentTitle}
        onClose={handleCloseDocumentPreview}
      />
    </>
  );
};

export default Completion;
